import { capitalize, Stack, Typography } from '@mui/material';

import { useDAL } from '@/contexts/useDAL';
import { ApplicationsLogosWithAliases } from '@/data/inventory/data';
import { PropertyType, type FilterProperty } from '@/types/advanceFilter';

import { LogTimeCell, SeverityCircle } from './logTimeCell';
import type { LogEntry } from './types';

import { CopyToClipboardWrapper } from '../../buttons/copyToClipboardButton';
import {
  CloudProviderCircle,
  CloudProviderIndicator,
} from '../../cloudProviderIndicator';
import { MultiSelectFilter } from '../../queryBuilder/filters';
import { CircleImage } from '../../shared/circleImage';
import { dataSourcesSchemas } from '../data/dataSourcesSchemas';

const eventTime: FilterProperty<LogEntry> = {
  name: 'eventTime',
  displayName: 'Log Time (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) => <LogTimeCell entry={row.original} />,
  },
  filter: {
    shouldHideBadge: () => true,
  },
};

const id: FilterProperty<LogEntry> = {
  name: 'id',
  displayName: 'ID',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.id && <Typography>{row.original.id}</Typography>,
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.id && (
          <CopyToClipboardWrapper textToCopy={entity.id}>
            <Typography>{entity.id}</Typography>
          </CopyToClipboardWrapper>
        )
      );
    },
  },
};

const content: FilterProperty<LogEntry> = {
  name: 'message',
  displayName: 'Content',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.message && (
        <Typography className='truncate' maxWidth='700px'>
          {row.original.message}
        </Typography>
      ),
  },
};

const fields: FilterProperty<LogEntry>[] = [
  'content',
  'eventVersion',
  'eventSource',
  'eventName',
  'awsRegion',
  'sourceIPAddress',
  'user',
  'query',
  'index',
  'took_ms',
  'hits',
  'remote_addr',
  'method',
  'path',
  'bytes_sent',
  'user_agent',
  'initiatedBy',
  'resource',
  'sourceIpAddress',
  'status',
  'provider',
  'product',
  'ruleName',
  'category',
  'computer',
  'account',
  'srcIpAddress',
  'srcCountry',
  'action',
  'operationName',
  'tenantId',
  'workspace',
]
  .sort()
  .map(
    (name): FilterProperty<LogEntry> => ({
      name,
      displayName: name,
      type: PropertyType.String,
      group: '',
      column: {
        cell: ({ row }) =>
          name in row.original && (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <Typography>{(row.original as any)[name]}</Typography>
          ),
      },
      field: {
        Component: ({ entity }) => {
          return (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            name in entity && <Typography>{(entity as any)[name]}</Typography>
          );
        },
      },
    })
  );

const tableName: FilterProperty<LogEntry> = {
  name: 'tableName',
  displayName: 'Table name',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.tableName && (
        <Typography>{row.original.tableName}</Typography>
      ),
  },
  field: {
    Component: ({ entity }) => {
      return entity.tableName && <Typography>{entity.tableName}</Typography>;
    },
  },
  filter: {
    Component: (props) => {
      return (
        <MultiSelectFilter
          {...props}
          options={Array.from(
            new Set(dataSourcesSchemas.map((x) => x.schemaName))
          )
            .sort()
            .map((x) => ({
              id: x,
              name: x,
            }))}
        />
      );
    },
  },
};

const sourcePath: FilterProperty<LogEntry> = {
  name: 'sourcePath',
  displayName: 'Source path',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.sourcePath && (
        <Typography>{row.original.sourcePath}</Typography>
      ),
  },
  field: {
    Component: ({ entity }) => {
      return entity.sourcePath && <Typography>{entity.sourcePath}</Typography>;
    },
  },
  filter: {
    Component: (props) => {
      return (
        <MultiSelectFilter
          {...props}
          options={Array.from(new Set(dataSourcesSchemas.map((x) => x.path)))
            .sort()
            .map((x) => ({
              id: x,
              name: x,
            }))}
        />
      );
    },
  },
};

const service: FilterProperty<LogEntry> = {
  name: 'service',
  displayName: 'Service',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.service && (
        <Stack direction='row' alignItems='center' gap='8px'>
          <CircleImage
            alt={row.original.service}
            src={ApplicationsLogosWithAliases[row.original.service]}
          />
          <Typography>{row.original.service}</Typography>
        </Stack>
      ),
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.service && (
          <Stack direction='row' alignItems='center' gap='8px'>
            <CircleImage
              alt={entity.service}
              src={ApplicationsLogosWithAliases[entity.service]}
            />
            <Typography>{entity.service}</Typography>
          </Stack>
        )
      );
    },
  },
  filter: {
    Component: (props) => {
      return (
        <MultiSelectFilter
          {...props}
          options={Array.from(new Set(dataSourcesSchemas.map((x) => x.appName)))
            .sort()
            .map((x) => ({
              Image: (
                <CircleImage alt={x!} src={ApplicationsLogosWithAliases[x!]} />
              ),
              id: x!,
              name: x!,
            }))}
        />
      );
    },
  },
};

const dataSource: FilterProperty<LogEntry> = {
  name: 'dataSource',
  displayName: 'Data source',
  type: PropertyType.String,
  group: '',
  column: {
    cell: ({ row }) =>
      row.original.cloudProvider &&
      row.original.dataSource && (
        <CloudProviderIndicator
          cloudProvider={row.original.cloudProvider}
          text={row.original.dataSource}
        />
      ),
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.cloudProvider &&
        entity.dataSource && (
          <CloudProviderIndicator
            cloudProvider={entity.cloudProvider}
            text={entity.dataSource}
          />
        )
      );
    },
  },
  filter: {
    Component: (props) => {
      const dal = useDAL();
      const { data } = dal.logs.useListSchemas([]);

      return (
        <MultiSelectFilter
          {...props}
          options={(data?.entries || []).map((x) => ({
            Image: x.cloudProvider && (
              <CloudProviderCircle cloudProvider={x.cloudProvider} />
            ),
            id: x.resourceName,
            name: x.resourceName,
          }))}
        />
      );
    },
  },
};

const level: FilterProperty<LogEntry> = {
  name: 'level',
  displayName: 'Level',
  type: PropertyType.String,
  group: '',
  column: {
    enableHiding: false,
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.level && (
          <Stack direction='row' alignItems='center' gap='12px'>
            <SeverityCircle severity={entity.level} />
            <Typography>
              {capitalize(entity.level.toLocaleLowerCase())}
            </Typography>
          </Stack>
        )
      );
    },
  },
  filter: {
    Component: (props) => {
      return (
        <MultiSelectFilter
          {...props}
          options={[
            {
              Image: <SeverityCircle severity='INFO' />,
              id: 'INFO',
              name: 'Info',
            },
            {
              Image: <SeverityCircle severity='WARN' />,
              id: 'WARN',
              name: 'Warning',
            },
            {
              Image: <SeverityCircle severity='ERROR' />,
              id: 'ERROR',
              name: 'Error',
            },
            {
              Image: <SeverityCircle severity='UNKNOWN' />,
              id: 'UNKNOWN',
              name: 'Unknown',
            },
          ]}
        />
      );
    },
    Badge({ conditionValue }) {
      if (conditionValue === 'INFO') {
        return 'Info';
      }
      if (conditionValue === 'WARN') {
        return 'Warning';
      }
      if (conditionValue === 'ERROR') {
        return 'Error';
      }
      if (conditionValue === 'UNKNOWN') {
        return 'Unknown';
      }
      return null;
    },
  },
};

export const logSpaceResultsTableHiddenColumns = [level, id, ...fields];

export const logSpaceResultsTableColumns: FilterProperty<LogEntry>[] = [
  level,
  id,
  eventTime,
  tableName,
  content,
  sourcePath,
  service,
  dataSource,
  ...fields,
];

export const logSpaceSummaryFields: FilterProperty<LogEntry>[] = [
  level,
  tableName,
  dataSource,
  service,
  sourcePath,
];

export const logSpaceDetailsFields: FilterProperty<LogEntry>[] = [
  id,
  eventTime,
  ...fields,
];
